import type { SerializedDockview } from "dockview"
import { NODE_TYPES, type SerializedGridNode } from "./dockview-grid-types"

interface LeafData {
  views?: string[]
  id?: string
  activeView?: string
}

function isLeafNode(
  node: { type: string, data: unknown },
): node is { type: "leaf", data: LeafData } {
  return node.type === NODE_TYPES.LEAF
}

function isBranchNode(
  node: { type: string, data: unknown },
): node is { type: "branch", data: SerializedGridNode[] } {
  return node.type === NODE_TYPES.BRANCH
}

/**
 * Recursively collects panel IDs from the Dockview grid in layout order.
 * - Leaf nodes: panel IDs from data.views (tab order within group)
 * - Branch nodes: traverse children left-to-right / top-to-bottom
 *
 * @param node - Grid node (leaf or branch)
 * @param acc - Accumulator for panel IDs
 * @returns Array of panel IDs in layout order
 */
function collectPanelOrder(node: SerializedGridNode, acc: string[] = []): string[] {
  if (isLeafNode(node)) {
    const views = node.data?.views
    if (Array.isArray(views)) {
      acc.push(...views)
    }
    return acc
  }

  if (isBranchNode(node)) {
    const children = node.data
    children.forEach((child) => collectPanelOrder(child, acc))
  }
  return acc
}

/**
 * Extracts panel IDs in layout order from a serialized Dockview layout.
 * Returns empty array for null/undefined or invalid layout.
 *
 * @param serialized - Serialized Dockview layout from api.toJSON()
 * @returns Panel IDs in left-to-right, top-to-bottom order
 */
export function getPanelOrderFromLayout(serialized: SerializedDockview | null | undefined): string[] {
  const root = serialized?.grid?.root
  if (!root) {
    return []
  }

  return collectPanelOrder(root, [])
}
